import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import ImageField from "../../components/admin/ImageField";

const toLines = (arr, keys) => (arr || []).map((x) => keys.map((k) => x[k]).join(" | ")).join("\n");
const fromLines = (text, keys) =>
  text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const parts = l.split("|").map((p) => p.trim());
    const obj = {};
    keys.forEach((k, i) => { obj[k] = parts[i] || ""; });
    return obj;
  });

const testimonialsToText = (arr) =>
  (arr || []).map((t) => `${t.quote}\n${t.name}\n${t.role}`).join("\n---\n");
const textToTestimonials = (text) =>
  text.split(/\n---\n/).map((b) => b.trim()).filter(Boolean).map((b) => {
    const [quote = "", name = "", role = ""] = b.split("\n").map((l) => l.trim());
    return { quote, name, role };
  });

export default function HomeTab() {
  const [form, setForm] = useState(null);

  useEffect(() => {
    api("/content/home")
      .then((h) =>
        setForm({
          hero_eyebrow: h.hero_eyebrow || "",
          hero_line1: h.hero_line1 || "",
          hero_line2: h.hero_line2 || "",
          hero_line3: h.hero_line3 || "",
          hero_subtext: h.hero_subtext || "",
          hero_image: h.hero_image || "",
          marquee_text: h.marquee_text || "",
          stats: toLines(h.stats, ["value", "label"]),
          gallery: toLines(h.gallery, ["src", "caption"]),
          ig_images: (h.ig_images || []).join("\n"),
          testimonials: testimonialsToText(h.testimonials),
        })
      )
      .catch((e) => toast.error(e.message));
  }, []);

  if (!form) return <p className="font-display italic text-gold">Loading</p>;
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    try {
      await api("/admin/content/home", {
        method: "PUT",
        body: {
          data: {
            hero_eyebrow: form.hero_eyebrow,
            hero_line1: form.hero_line1,
            hero_line2: form.hero_line2,
            hero_line3: form.hero_line3,
            hero_subtext: form.hero_subtext,
            hero_image: form.hero_image,
            marquee_text: form.marquee_text,
            stats: fromLines(form.stats, ["value", "label"]),
            gallery: fromLines(form.gallery, ["src", "caption"]),
            ig_images: form.ig_images.split("\n").map((l) => l.trim()).filter(Boolean),
            testimonials: textToTestimonials(form.testimonials),
          },
        },
      });
      toast.success("Home page updated. It is live now.");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const field = (key, label, testid, textarea = false, rows = 3, hint = "") => (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">{label}</label>
      {hint && <p className="text-xs text-neutral-500 mb-2">{hint}</p>}
      {textarea ? (
        <textarea data-testid={testid} rows={rows} value={form[key]} onChange={(e) => set(key, e.target.value)}
          className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white font-mono" />
      ) : (
        <input data-testid={testid} value={form[key]} onChange={(e) => set(key, e.target.value)}
          className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white" />
      )}
    </div>
  );

  return (
    <div data-testid="home-tab">
      <h2 className="font-display text-3xl text-ink">Home Page</h2>
      <p className="mt-2 text-sm text-neutral-500">Edit the hero, stats, marquee, gallery, Instagram strip and testimonials.</p>

      <div className="mt-8 space-y-6">
        {field("hero_eyebrow", "Hero Eyebrow", "home-eyebrow-input")}
        <div className="grid md:grid-cols-3 gap-5">
          {field("hero_line1", "Headline Line 1", "home-line1-input")}
          {field("hero_line2", "Headline Line 2 (italic gold)", "home-line2-input")}
          {field("hero_line3", "Headline Line 3", "home-line3-input")}
        </div>
        {field("hero_subtext", "Hero Subtext", "home-subtext-input", true, 3)}
        <ImageField label="Hero Image" value={form.hero_image} onChange={(v) => set("hero_image", v)} testid="home-image-input" />
        {field("marquee_text", "Marquee Text", "home-marquee-input")}
        {field("stats", "Legacy Stats", "home-stats-input", true, 4, "One per line: value | label. Example: 2011 | Founded in Mumbai")}
        {field("gallery", "Gallery Images", "home-gallery-input", true, 8, "One per line: image URL | caption")}
        {field("ig_images", "Instagram Strip Images", "home-ig-input", true, 6, "One image URL per line. First six are shown.")}
        {field("testimonials", "Testimonials", "home-testimonials-input", true, 10, "Each testimonial is three lines: quote, name, role. Separate testimonials with a line containing only ---")}

        <button onClick={save} data-testid="home-save-button"
          className="bg-ink text-cream px-8 py-3.5 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-gold transition-colors duration-300">
          Save Home Page
        </button>
      </div>
    </div>
  );
}
